import { clientOrigin, marketingOrigin } from '../common/siteEnvironment';
import React, { useState, useEffect } from 'react';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { signInWithMfa } from './mfaDialogs';
import { MFA_LOGIN_NOTICE } from '../common/mfa';

interface ManagementGatewayProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onSignUp: (email: string, password: string, name: string) => Promise<void>;
}

const ManagementGateway: React.FC<ManagementGatewayProps> = ({ onLogin, onSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(() => sessionStorage.getItem(MFA_LOGIN_NOTICE));
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const auth = getAuth();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setInfo(null);
    try {
      sessionStorage.removeItem(MFA_LOGIN_NOTICE);
      await signInWithMfa(email, password);
      await onLogin(email, password);
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError('Incorrect credentials. This portal is restricted to authorised Eprims Ltd personnel only.');
      } else {
        setError(err.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address first.'); return; }
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, email, { url: window.location.origin, handleCodeInApp: false });
      setResetSent(true);
      setError(null);
    } catch {
      setError('Could not send reset email. Please contact your system administrator.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, -apple-system, sans-serif', background: '#0a1120', color: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        .mg-grid-bg { position:fixed; inset:0; background-image:linear-gradient(rgba(99,102,241,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,0.05) 1px,transparent 1px); background-size:56px 56px; pointer-events:none; z-index:0; animation:mgGrid 20s linear infinite; }
        @keyframes mgGrid { from{background-position:0 0} to{background-position:56px 56px} }
        .mg-glow { position:fixed; inset:0; pointer-events:none; z-index:0; background:radial-gradient(ellipse 600px 500px at 5% 20%,rgba(99,102,241,0.08) 0%,transparent 60%),radial-gradient(ellipse 500px 400px at 95% 80%,rgba(139,92,246,0.06) 0%,transparent 60%); }
        .mg-nav { position:fixed; top:0; left:0; right:0; z-index:100; height:68px; display:flex; align-items:center; justify-content:space-between; padding:0 2.5rem; background:rgba(5,8,18,0.9); backdrop-filter:blur(20px); border-bottom:1px solid rgba(99,102,241,0.2); }
        .mg-nav-brand { display:flex; align-items:center; gap:14px; }
        .mg-nav-logo { width:42px; height:42px; border-radius:50%; overflow:hidden; box-shadow:0 0 0 2px rgba(99,102,241,0.35), 0 0 16px rgba(99,102,241,0.2); }
        .mg-nav-logo img { width:100%; height:100%; object-fit:cover; display:block; }
        .mg-nav-name { font-size:1rem; font-weight:900; letter-spacing:-.01em; }
        .mg-nav-name .accent { background:linear-gradient(90deg,#818cf8,#a78bfa); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        .mg-nav-sub { font-size:.6rem; font-weight:700; color:#6366f1; letter-spacing:.1em; text-transform:uppercase; margin-top:2px; }
        .mg-nav-right { display:flex; align-items:center; gap:1.25rem; }
        .mg-restricted-badge { display:inline-flex; align-items:center; gap:6px; padding:.3rem .9rem; background:rgba(239,68,68,.1); border:1px solid rgba(239,68,68,.3); border-radius:999px; font-size:.7rem; font-weight:700; color:#fca5a5; }
        .mg-lock-dot { width:6px; height:6px; background:#ef4444; border-radius:50%; animation:mgPulse 2s infinite; }
        @keyframes mgPulse { 0%,100%{opacity:1} 50%{opacity:.3} }
        .mg-nav-link { font-size:.8rem; font-weight:600; color:#64748b; text-decoration:none; transition:color .2s; }
        .mg-nav-link:hover { color:#f8fafc; }
        .mg-hero { min-height:100vh; display:grid; grid-template-columns:1fr 400px; gap:4rem; align-items:center; padding:8rem 2.5rem 4rem; max-width:1200px; margin:0 auto; position:relative; z-index:1; }
        .mg-badge { display:inline-flex; align-items:center; gap:7px; background:rgba(99,102,241,.1); border:1px solid rgba(99,102,241,.3); padding:6px 16px; border-radius:999px; font-size:.72rem; font-weight:700; color:#818cf8; text-transform:uppercase; letter-spacing:.08em; margin-bottom:1.5rem; }
        .mg-title { font-size:clamp(2rem,4vw,3.2rem); font-weight:900; line-height:1.08; letter-spacing:-.03em; margin-bottom:1.25rem; }
        .mg-title .grad { background:linear-gradient(135deg,#818cf8,#a78bfa); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        .mg-sub { font-size:.97rem; color:#94a3b8; line-height:1.8; margin-bottom:2rem; max-width:520px; }
        .mg-warning { display:flex; align-items:flex-start; gap:.75rem; padding:1rem 1.25rem; background:rgba(239,68,68,.07); border:1px solid rgba(239,68,68,.2); border-radius:12px; margin-bottom:1.5rem; }
        .mg-warning-icon { font-size:1rem; flex-shrink:0; margin-top:1px; }
        .mg-warning-text { font-size:.82rem; color:#fca5a5; line-height:1.65; }
        .mg-warning-text strong { color:#fff; }
        .mg-links { display:flex; gap:.75rem; flex-wrap:wrap; }
        .mg-link { display:inline-flex; align-items:center; gap:.4rem; padding:.45rem .9rem; border:1px solid rgba(99,102,241,.25); border-radius:8px; font-size:.78rem; font-weight:600; color:#94a3b8; text-decoration:none; transition:all .2s; }
        .mg-link:hover { border-color:#6366f1; color:#818cf8; }
        .mg-card { background:rgba(10,15,30,0.95); border:1px solid rgba(99,102,241,0.25); border-radius:20px; padding:2.25rem; backdrop-filter:blur(20px); box-shadow:0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.08); }
        .mg-card-header { text-align:center; margin-bottom:1.75rem; }
        .mg-card-logo { width:60px; height:60px; border-radius:50%; overflow:hidden; margin:0 auto .75rem; box-shadow:0 0 0 2px rgba(99,102,241,0.3), 0 0 20px rgba(99,102,241,0.2); }
        .mg-card-logo img { width:100%; height:100%; object-fit:cover; }
        .mg-card-title { font-size:1.05rem; font-weight:800; color:#fff; margin-bottom:.2rem; }
        .mg-card-sub { font-size:.78rem; color:#64748b; }
        .mg-form-group { margin-bottom:1rem; }
        .mg-label { display:block; font-size:.7rem; font-weight:700; color:#64748b; margin-bottom:.4rem; text-transform:uppercase; letter-spacing:.07em; }
        .mg-input { width:100%; background:rgba(5,8,18,0.9); border:1px solid rgba(99,102,241,.2); border-radius:9px; padding:.7rem 1rem; color:#fff; font-size:.9rem; font-family:Inter,sans-serif; transition:border-color .2s; outline:none; box-sizing:border-box; }
        .mg-input:focus { border-color:#6366f1; }
        .mg-input::placeholder { color:#475569; }
        .mg-btn { width:100%; padding:.85rem; background:linear-gradient(135deg,#6366f1,#4f46e5); color:#fff; font-size:.95rem; font-weight:700; border:none; border-radius:10px; cursor:pointer; transition:all .2s; font-family:Inter,sans-serif; box-shadow:0 4px 16px rgba(99,102,241,0.35); display:flex; align-items:center; justify-content:center; gap:.5rem; }
        .mg-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 24px rgba(99,102,241,0.5); }
        .mg-btn:disabled { opacity:.6; cursor:not-allowed; transform:none; }
        .mg-alert-err { padding:.75rem 1rem; border-radius:9px; font-size:.82rem; line-height:1.6; margin-bottom:1rem; background:rgba(239,68,68,.12); border:1px solid rgba(239,68,68,.3); color:#fca5a5; }
        .mg-alert-info { padding:.75rem 1rem; border-radius:9px; font-size:.82rem; line-height:1.6; margin-bottom:1rem; background:rgba(99,102,241,.12); border:1px solid rgba(99,102,241,.3); color:#a5b4fc; }
        .mg-forgot { text-align:right; margin-top:.2rem; margin-bottom:.2rem; }
        .mg-forgot button { background:none; border:none; color:#6366f1; cursor:pointer; font-size:.75rem; font-weight:600; }
        .mg-forgot button:hover { color:#818cf8; }
        .mg-card-footer { margin-top:1.25rem; padding-top:1rem; border-top:1px solid rgba(99,102,241,0.15); text-align:center; font-size:.72rem; color:#475569; line-height:1.6; }
        .mg-card-footer a { color:#6366f1; text-decoration:none; }
        .mg-spinner { width:18px; height:18px; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:mgSpin .7s linear infinite; }
        @keyframes mgSpin { to{transform:rotate(360deg)} }
        .mg-footer { background:rgba(3,5,12,0.95); border-top:1px solid rgba(99,102,241,0.15); padding:2rem 2.5rem; position:relative; z-index:1; }
        .mg-footer-inner { max-width:1200px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
        .mg-footer-copy { font-size:.8rem; color:#475569; }
        .mg-footer-links { display:flex; gap:1.5rem; }
        .mg-footer-links a { color:#475569; text-decoration:none; font-size:.8rem; transition:color .2s; }
        .mg-footer-links a:hover { color:#6366f1; }
        @media(max-width:1024px) { .mg-hero { grid-template-columns:1fr; min-height:auto; padding-top:6rem; gap:2.5rem; } }
        @media(max-width:768px) { .mg-nav { padding:0 1.25rem; } .mg-hero { padding:5.5rem 1.25rem 3rem; } }
      `}</style>

      {/* Background layers */}
      <div className="mg-grid-bg" />
      <div className="mg-glow" />

      {/* ── Navigation ── */}
      <nav className="mg-nav">
        <div className="mg-nav-brand">
          <div className="mg-nav-logo">
            <img src="/eprims-logo.png" alt="Eprims"
              onError={(e) => {
                const el = e.target as HTMLImageElement;
                el.style.display = 'none';
                if (el.parentElement) {
                  el.parentElement.style.background = 'linear-gradient(135deg,#6366f1,#8b5cf6)';
                  el.parentElement.innerHTML = '<span style="color:white;font-weight:900;font-size:.9rem;display:flex;align-items:center;justify-content:center;height:100%">EP</span>';
                }
              }}
            />
          </div>
          <div>
            <div className="mg-nav-name"><span className="accent">EPRIMS</span> Ltd</div>
            <div className="mg-nav-sub">Management Portal</div>
          </div>
        </div>
        <div className="mg-nav-right">
          <div className="mg-restricted-badge">
            <span className="mg-lock-dot" />
            Restricted Access
          </div>
          <a href={clientOrigin} className="mg-nav-link">Client Portal</a>
          <a href={marketingOrigin} className="mg-nav-link">eprims.com</a>
        </div>
      </nav>

      {/* ── Hero + Login ── */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="mg-hero">

          {/* Left: Info */}
          <div>
            <div className="mg-badge">🔐 Eprims Ltd — Internal Staff Portal</div>
            <h1 className="mg-title">
              Company Management<br />
              <span className="grad">Command Centre</span>
            </h1>
            <p className="mg-sub">
              This portal is for authorised Eprims Ltd personnel only. Access global operations, regional dashboards, client management, subscription oversight, and internal reporting tools.
            </p>

            <div className="mg-warning">
              <span className="mg-warning-icon">⚠️</span>
              <div className="mg-warning-text">
                <strong>Restricted Access.</strong> This portal is for Eprims Ltd staff only. Unauthorised access attempts are logged. If you are a client, please use the <a href={clientOrigin} style={{ color: '#93c5fd', textDecoration: 'none' }}>IntelOptics Client Portal</a> instead.
              </div>
            </div>

            <div className="mg-links">
              <a href={clientOrigin} className="mg-link">
                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                Client Portal ({new URL(clientOrigin).hostname})
              </a>
              <a href={marketingOrigin} className="mg-link">
                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                Company Site (eprims.com)
              </a>
              <a href="mailto:contact@eprims.com" className="mg-link">
                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                IT Support
              </a>
            </div>
          </div>

          {/* Right: Login card */}
          <div className="mg-card">
            <div className="mg-card-header">
              <div className="mg-card-logo">
                <img src="/eprims-logo.png" alt="Eprims"
                  onError={(e) => {
                    const el = e.target as HTMLImageElement;
                    el.style.display = 'none';
                    if (el.parentElement) {
                      el.parentElement.style.background = 'linear-gradient(135deg,#6366f1,#8b5cf6)';
                      el.parentElement.innerHTML = '<span style="color:white;font-weight:900;font-size:.9rem;display:flex;align-items:center;justify-content:center;height:100%">EP</span>';
                    }
                  }}
                />
              </div>
              <div className="mg-card-title">Staff Sign In</div>
              <div className="mg-card-sub">Eprims Ltd — Authorised Personnel Only</div>
            </div>

            {error && <div className="mg-alert-err">{error}</div>}
            {info && <div role="status" className="mg-alert-info">{info}</div>}

            {showReset ? (
              <form onSubmit={handlePasswordReset}>
                <p style={{ fontSize: '.82rem', color: '#94a3b8', marginBottom: '1rem', lineHeight: 1.6 }}>Enter your Eprims staff email address to receive a password reset link.</p>
                <div className="mg-form-group">
                  <label className="mg-label">Staff Email Address</label>
                  <input className="mg-input" type="email" placeholder="name@eprims.com" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                {resetSent && <div className="mg-alert-info">Reset link sent to {email}. Check your inbox.</div>}
                <button type="submit" className="mg-btn" disabled={isLoading || resetSent}>
                  {isLoading ? <span className="mg-spinner" /> : 'Send Reset Link'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '.75rem' }}>
                  <button type="button" onClick={() => { setShowReset(false); setResetSent(false); setError(null); }} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '.8rem', fontWeight: 600 }}>← Back to Sign In</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className="mg-form-group">
                  <label className="mg-label">Staff Email Address</label>
                  <input className="mg-input" type="email" placeholder="name@eprims.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="off" required />
                </div>
                <div className="mg-form-group">
                  <label className="mg-label">Password</label>
                  <input className="mg-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
                  <div className="mg-forgot">
                    <button type="button" onClick={() => { setShowReset(true); setError(null); }}>Forgot Password?</button>
                  </div>
                </div>
                <button type="submit" className="mg-btn" disabled={isLoading}>
                  {isLoading ? <span className="mg-spinner" /> : '🔐 Sign In to Management Portal'}
                </button>
              </form>
            )}

            <div className="mg-card-footer">
              🔒 Secured by Firebase Authentication<br />
              Unauthorised access attempts are monitored and logged.<br />
              Issues? <a href="mailto:contact@eprims.com">contact@eprims.com</a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="mg-footer">
        <div className="mg-footer-inner">
          <div className="mg-footer-copy">
            <strong style={{ color: '#94a3b8' }}>Eprims Ltd Management Portal</strong> — Restricted to authorised staff<br />
            © 2026 Eprims Ltd. Penn Valley, PA, USA
          </div>
          <div className="mg-footer-links">
            <a href={marketingOrigin}>eprims.com</a>
            <a href={clientOrigin}>Client Portal</a>
            <a href="mailto:contact@eprims.com">IT Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ManagementGateway;
